import { Dispatch } from "redux";
import { enqueueSnackbar } from "notistack";
import { organizationMembershipService } from "../../api";
import { getAxiosErrorMessage } from "../../api/axiosError";
import {
  OrganizationMembershipData,
  OrganizationMembershipUserData,
} from "../../models/organizationMembershipData";
import {
  ACCEPT_MEMBERSHIP_FAILURE,
  ACCEPT_MEMBERSHIP_REQUEST,
  ACCEPT_MEMBERSHIP_SUCCESS,
  CREATE_MEMBERSHIP_ADD_FAILURE,
  CREATE_MEMBERSHIP_ADD_REQUEST,
  CREATE_MEMBERSHIP_ADD_SUCCESS,
  FETCH_MEMBERSHIPS_FAILURE,
  FETCH_MEMBERSHIPS_REQUEST,
  FETCH_MEMBERSHIPS_SUCCESS,
  MembershipActionTypes,
  PATCH_MEMBERSHIP_ADMIN_STATUS_FAILURE,
  PATCH_MEMBERSHIP_ADMIN_STATUS_REQUEST,
  PATCH_MEMBERSHIP_ADMIN_STATUS_SUCCESS,
  REMOVE_MEMBERSHIP_FAILURE,
  REMOVE_MEMBERSHIP_REQUEST,
  REMOVE_MEMBERSHIP_SUCCESS,
  SET_MEMBERSHIPS_LOADED,
  SetMembershipsLoadedAction,
} from "./types";
import { getDynamicTranslation } from "../../components/core/localization/localizationUtils";

// Action creator to initiate the fetch memberships request
export const fetchMembershipsRequest = (): MembershipActionTypes => ({
  type: FETCH_MEMBERSHIPS_REQUEST,
});

// Action creator to handle successful fetch memberships response
export const fetchMembershipsSuccess = (
  memberships: OrganizationMembershipData[]
): MembershipActionTypes => ({
  type: FETCH_MEMBERSHIPS_SUCCESS,
  payload: memberships,
});

// Action creator to handle failed fetch memberships response
export const fetchMembershipsFailure = (error: string): MembershipActionTypes => ({
  type: FETCH_MEMBERSHIPS_FAILURE,
  error,
});

// Action creator to initiate the add membership request
export const createMembershipAddRequest = (): MembershipActionTypes => ({
  type: CREATE_MEMBERSHIP_ADD_REQUEST,
});

// Action creator to handle successful adding membership
export const createMembershipAddSuccess = (): MembershipActionTypes => ({
  type: CREATE_MEMBERSHIP_ADD_SUCCESS,
});

// Action creator to handle failed adding membership
export const createMembershipAddFailure = (error: string): MembershipActionTypes => ({
  type: CREATE_MEMBERSHIP_ADD_FAILURE,
  error,
});

export const removeMembershipRequest = (): MembershipActionTypes => ({
  type: REMOVE_MEMBERSHIP_REQUEST,
});

export const removeMembershipSuccess = (
  membershipId: string
): MembershipActionTypes => ({
  type: REMOVE_MEMBERSHIP_SUCCESS,
  membershipId,
});

export const removeMembershipFailure = (error: string): MembershipActionTypes => ({
  type: REMOVE_MEMBERSHIP_FAILURE,
  error,
});

// Action creator to initiate the accept membership request
export const acceptMembershipRequest = (): MembershipActionTypes => ({
  type: ACCEPT_MEMBERSHIP_REQUEST,
});

// Action creator to handle successful acceptance of membership
export const acceptMembershipSuccess = (
  membershipId: string
): MembershipActionTypes => ({
  type: ACCEPT_MEMBERSHIP_SUCCESS,
  membershipId,
});

// Action creator to handle failed acceptance of membership
export const acceptMembershipFailure = (error: string): MembershipActionTypes => ({
  type: ACCEPT_MEMBERSHIP_FAILURE,
  error,
});

export function setMembershipsLoaded(): SetMembershipsLoadedAction {
  return {
    type: SET_MEMBERSHIPS_LOADED,
  };
}

export const patchMembershipAdminStatusRequest = (): MembershipActionTypes => ({
  type: PATCH_MEMBERSHIP_ADMIN_STATUS_REQUEST,
});

export const patchMembershipAdminStatusSuccess = (
  membershipId: string
): MembershipActionTypes => ({
  type: PATCH_MEMBERSHIP_ADMIN_STATUS_SUCCESS,
  membershipId,
});

export const patchMembershipAdminStatusFailure = (
  error: string
): MembershipActionTypes => ({
  type: PATCH_MEMBERSHIP_ADMIN_STATUS_FAILURE,
  error,
});

/**
 * Action creator to initiate the asynchronous fetching of memberships.
 * Dispatches appropriate actions based on the success or failure of the fetch operation.
 * @returns {ThunkAction<void, {}, unknown, MembershipActionTypes>} A thunk action.
 */
export const fetchMemberships = () => {
  return async (dispatch: Dispatch<MembershipActionTypes>) => {
    dispatch(fetchMembershipsRequest());
    try {
      const response = await organizationMembershipService.getOrganizationMemberships();
      dispatch(fetchMembershipsSuccess(response));
    } catch (error) {
      const errorMessage = getAxiosErrorMessage(
        error,
        getDynamicTranslation("error.fetchMembershipsError", "organization")
      );
      dispatch(fetchMembershipsFailure(errorMessage));
      enqueueSnackbar(errorMessage, { variant: "error" });
    }
  };
};

/**
 * Action creator to initiate the asynchronous fetching of memberships by organization ID.
 * Dispatches appropriate actions based on the success or failure of the fetch operation.
 * @param {string} organizationId - The ID of the organization.
 * @returns {ThunkAction<void, {}, unknown, MembershipActionTypes>} A thunk action.
 */
export const fetchMembershipsByOrganizationId = (organizationId: string) => {
  return async (dispatch: Dispatch<MembershipActionTypes>) => {
    dispatch(fetchMembershipsRequest());
    try {
      const response =
        await organizationMembershipService.getMembershipsByOrganizationId(
          organizationId
        );
      dispatch(fetchMembershipsSuccess(response));
    } catch (error) {
      const errorMessage = getAxiosErrorMessage(
        error,
        getDynamicTranslation("error.fetchMembershipByOrgaID", "organization")
      );
      dispatch(fetchMembershipsFailure(errorMessage));
      enqueueSnackbar(errorMessage, { variant: "error" });
    }
  };
};

/**
 * Action creator to initiate the asynchronous creation of memberships.
 * Dispatches appropriate actions based on the success or failure of the create operation.
 * @param {OrganizationMembershipUserData} userData - The data for the memberships user.
 * @param suppressSnackbars
 * @returns {ThunkAction<void, {}, unknown, MembershipActionTypes>} A thunk action.
 */
export const createUserMembership = (
  userData: OrganizationMembershipUserData,
  suppressSnackbars = false
) => {
  return async (dispatch: Dispatch<MembershipActionTypes>) => {
    dispatch(createMembershipAddRequest());
    try {
      await organizationMembershipService.addUserToOrganization(
        userData,
        suppressSnackbars
      );
      await dispatch(createMembershipAddSuccess());

      enqueueSnackbar(
        getDynamicTranslation("backendResponses.success.membershipAdded", "snackbars"),
        {
          variant: "success",
        }
      );
      return true;
    } catch (error) {
      const errorMessage = getDynamicTranslation(
        "error.membershipExists",
        "organization"
      );
      dispatch(createMembershipAddFailure(errorMessage));
      return { success: false, error };
    }
  };
};

/**
 * Action creator for creating bulk memberships.
 * @param {OrganizationMembershipUserData} userData - The data for the bulk membership creation.
 * @returns {Promise<boolean>} A promise that resolves to true if the memberships are successfully created, otherwise false.
 */
export const createUserBulkMembership = (userData: OrganizationMembershipUserData) => {
  return async (dispatch: Dispatch<MembershipActionTypes>) => {
    dispatch(createMembershipAddRequest());
    try {
      await organizationMembershipService.addUserToOrganization(userData, true);
      await dispatch(createMembershipAddSuccess());
      return true;
    } catch (error) {
      return { success: false, error };
    }
  };
};

/**
 * Action creator for removing a membership.
 * @param {string} membershipId - The ID of the membership to remove.
 * @returns {Promise<void>} A promise that resolves when the membership is successfully removed.
 */
export const removeMembership = (membershipId: string) => {
  return async (dispatch: Dispatch<MembershipActionTypes>) => {
    dispatch(removeMembershipRequest());
    try {
      await organizationMembershipService.removeMembership(membershipId);
      dispatch(removeMembershipSuccess(membershipId));
      enqueueSnackbar(
        getDynamicTranslation("backendResponses.success.membershipRemoved", "snackbars"),
        {
          variant: "success",
        }
      );
    } catch (error) {
      dispatch(
        removeMembershipFailure(
          getDynamicTranslation("error.removeMembership", "organization")
        )
      );
    }
  };
};

/**
 * Action creator for patching the admin status of a membership.
 * @param {string} membershipId - The ID of the membership to be updated.
 * @param {object} data - The admin data to be updated with boolean is_admin.
 * @returns {Promise<void>} A promise that resolves when the membership's admin status is successfully updated
 */
export const patchMembershipAdminStatus = (
  membershipId: string,
  data: { is_admin: boolean }
) => {
  return async (dispatch: Dispatch<MembershipActionTypes>) => {
    dispatch(patchMembershipAdminStatusRequest());
    try {
      await organizationMembershipService.patchMembershipAdminStatus(membershipId, data);
      dispatch(patchMembershipAdminStatusSuccess(membershipId));
      enqueueSnackbar(
        getDynamicTranslation("organization.adminStatusSuccess", "snackbars"),
        {
          variant: "success",
        }
      );
    } catch (error) {
      dispatch(
        patchMembershipAdminStatusFailure(
          getDynamicTranslation("organization.adminStatusError", "snackbars")
        )
      );
    }
  };
};
